import {
	animate,
	AnimatePresence,
	cubicBezier,
	motion,
	useMotionValue,
	useMotionValueEvent,
	useTransform,
} from 'motion/react'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import {
	LibraryVocabulary,
	PracticeSet,
	PracticeSetAnswer,
	PracticeSetItemVocabulary,
	PracticeVocabularyLearningProgressType,
} from '@/models.ts'
import { pronounce } from '@/lib/utils.ts'
import { ChevronLeft, ChevronRight, CornerUpLeft, CornerUpRight, Loader2, RefreshCcw, Volume2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import useGlobalStore from '@/useGlobalStore.ts'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.tsx'
import LearningStageCircle from '@/components/LearningStageCircle.tsx'
import { useTheme } from '@/useTheme.ts'
import api from '@/api.ts'
import AnkiResultCardStack from '@/components/anki-result-card-stack.tsx'
import useWordDetailsModalStore from '@/useWordDetailsModalStore.ts'
import PracticeSetResultSummary from '@/components/practice-set-result-summary.tsx'
import LibraryVocabularyLearningsPracticeResult from '@/components/library-vocabulary-learnings-practice-result.tsx'

type CardSwipeDirection = 'left' | 'right'

const easeOutExpo = [0.16, 1, 0.3, 1]

interface AnkiCardProps {
	indexInStack: number
	isLast: boolean
	data: PracticeSetItemVocabulary
	setIsDragging: Dispatch<SetStateAction<any>>
	isDragging: boolean
	setDirection: Dispatch<SetStateAction<any>>
}

const AnkiCard = ({ indexInStack, isLast, data, setIsDragging, isDragging, setDirection }: AnkiCardProps) => {
	const { t } = useTranslation()
	const x = useMotionValue(0)
	const rotateY = useMotionValue(0)
	const { themeColor } = useTheme()
	const [isFlipped, setIsFlipped] = useState(false)
	const [isDragStarted, setIsDragStarted] = useState(false)
	const { learningLanguageCode, isTTSSupported } = useGlobalStore()

	const offsetBoundary = 150

	const inputX = [offsetBoundary * -1, 0, offsetBoundary]
	const outputY = [50, 0, 50]
	const outputRotate = [-40, 0, 40]
	const outputMainBgColor = useMemo(
		() => ['#ff0000', themeColor === 'dark' ? '#3d3d3d' : '#e1e1e1', '#32cd32'],
		[themeColor],
	)

	const drivenY = useTransform(x, inputX, outputY)
	const drivenRotation = useTransform(x, inputX, outputRotate)
	const drivenBg = useTransform(x, [-40, 0, 40], outputMainBgColor)

	useMotionValueEvent(rotateY, 'change', (latest) => {
		if (!isFlipped && latest >= 90) {
			setIsFlipped(true)
		} else if (isFlipped && latest <= 90) {
			setIsFlipped(false)
		}
	})

	const flipCard = () => {
		if (isLast && !isDragStarted && !isFlipped) {
			animate(rotateY, rotateY.get() === 0 ? 180 : 0, {
				duration: 0.6,
				ease: [0.42, 0, 0.58, 1],
			})
		}
	}

	const pronounceText = async (event: any) => {
		event.stopPropagation()
		if (!isTTSSupported) {
			toast(t('Speaking is not supported for this language.'))
			return
		}
		await pronounce(data.vocabulary.word, learningLanguageCode)
	}

	return (
		<>
			{/* Card Visuals and Flip Animation */}
			<motion.div
				id={`cardDrivenWrapper-${data.id}`}
				className={`absolute rounded-lg text-center w-full aspect-[100/150] origin-bottom shadow-card select-none ${
					!isDragging ? 'hover:cursor-grab' : ''
				} ${themeColor === 'dark' ? 'dark:text-gray-100' : 'text-black'}`}
				onClick={flipCard}
				drag="x"
				dragSnapToOrigin
				dragElastic={0.35}
				dragConstraints={{ left: 0, right: 0 }}
				dragTransition={{ bounceStiffness: 1000, bounceDamping: 50 }}
				onDragStart={() => {
					setIsDragging(true)
					setIsDragStarted(true)
				}}
				onDragEnd={(_, info) => {
					setIsDragging(false)
					setIsDragStarted(false)
					const isOffBoundary = info.offset.x > offsetBoundary || info.offset.x < -offsetBoundary
					const direction = info.offset.x > 0 ? 'right' : 'left'
					if (isOffBoundary) {
						setDirection(direction)
					}
				}}
				style={{
					x,
					y: drivenY,
					rotate: drivenRotation,
					backgroundColor: drivenBg,
					rotateY,
					transformStyle: 'preserve-3d',
				}}>
				{/* Front Face */}
				<div
					className="card-face front absolute w-full h-full"
					style={{
						backfaceVisibility: 'hidden',
						WebkitBackfaceVisibility: 'hidden',
						transform: 'rotateY(0deg) translateZ(0.1px)',
						WebkitTransform: 'rotateY(0deg) translateZ(0.1px)',
						transformStyle: 'preserve-3d',
						WebkitTransformStyle: 'preserve-3d',
					}}>
					<div className="w-full p-4 pt-2 h-16 flex flex-row justify-between">
						<div className="flex justify-center items-center">
							<span className="font-semibold text-xl">{indexInStack}</span>
						</div>
						<div className="flex flex-none w-10 -me-2">
							<LearningStageCircle learningStage={data.vocabulary.learning_stage} />
						</div>
					</div>
					<div className="mt-10">
						<div className="flex justify-center items-center mb-1">
							<Button
								onClick={pronounceText}
								variant="ghost"
								className={!isTTSSupported ? 'opacity-50' : ''}
								size="icon">
								<Volume2 className="h-5 w-5" />
							</Button>
						</div>
						<h6 className="font-semibold text-2xl">{data.vocabulary.word}</h6>
					</div>
					{isLast && (
						<div className="bottom-0 py-2 absolute w-full flex flex-col justify-center items-center">
							<RefreshCcw className="w-4 h-4 mb-1" />
							<div className="text-sm">{t('Tap the card to flip')}</div>
						</div>
					)}
				</div>
				{/* Back Face */}
				<div
					className="card-face back absolute w-full h-full"
					style={{
						backfaceVisibility: 'hidden',
						transform: 'rotateY(180deg)',
					}}>
					<div className="w-full p-4 pt-2 h-16 flex flex-row justify-between">
						<div className="flex justify-center items-center">
							<span className="font-semibold text-xl">{indexInStack}</span>
						</div>
						<div></div>
					</div>
					<div className="mt-10">
						<div className="flex justify-center items-center mb-1">
							<Button
								onClick={pronounceText}
								variant="ghost"
								className={!isTTSSupported ? 'opacity-50' : ''}
								size="icon">
								<Volume2 className="h-5 w-5" />
							</Button>
						</div>
						<h6 className="font-semibold text-2xl">{data.vocabulary.word}</h6>
						<h4 className="text-xl mt-4">{data.vocabulary.translation}</h4>
					</div>
					<div className="bottom-0 py-2 absolute w-full flex flex-row" dir="ltr">
						<div className="flex items-center justify-center flex-col">
							<div className="px-6 w-full flex justify-start">
								<CornerUpLeft className="w-6 h-6" />
							</div>
							<div className="pe-6 ps-2 text-sm">{t('Swipe left if you were wrong')}</div>
						</div>
						<div className="flex items-center justify-center flex-col">
							<div className="px-6 w-full flex justify-end">
								<CornerUpRight className="w-6 h-6" />
							</div>
							<div className="ps-6 pe-2 text-sm">{t('Swipe right if you were right')}</div>
						</div>
					</div>
				</div>
			</motion.div>
		</>
	)
}

interface AnkiCardsContainerProps {
	cards: PracticeSetItemVocabulary[]
	onCardSwiped: (card: PracticeSetItemVocabulary, direction: string) => void
}

const AnkiCardsContainer = ({ cards, onCardSwiped }: AnkiCardsContainerProps) => {
	const [direction, setDirection] = useState<CardSwipeDirection | ''>('')
	const [isDragging, setIsDragging] = useState(false)

	useEffect(() => {
		if (['left', 'right'].includes(direction)) {
			onCardSwiped(cards[cards.length - 1], direction)
		}
		setDirection('')
	}, [direction])

	const cardVariants = {
		current: {
			opacity: 1,
			y: 0,
			scale: 1,
			transition: { duration: 0.3, ease: easeOutExpo },
		},
		upcoming: {
			opacity: 0.5,
			y: 67,
			scale: 0.9,
			transition: { duration: 0.3, ease: easeOutExpo, delay: 0 },
		},
		remainings: {
			opacity: 0,
			y: 20,
			scale: 0.9,
		},
		exit: {
			opacity: 0,
			x: direction === 'left' ? -300 : 300,
			y: 40,
			rotate: direction === 'left' ? -20 : 20,
			transition: { duration: 0.3, ease: easeOutExpo },
		},
	}

	return (
		<motion.div
			className={`flex p-5 mt-5 z-[1000] flex-col justify-center items-center h-full ${
				isDragging ? 'cursor-grabbing' : ''
			}`}
			style={{ perspective: 1000 }}>
			<div className="flex flex-col gap-6 w-full items-center justify-center relative z-10">
				<div className="w-full aspect-[100/150] max-w-xs mb-[20px] relative z-10">
					<AnimatePresence>
						{cards.map((card, i) => {
							const isLast = i === cards.length - 1
							const isUpcoming = i === cards.length - 2
							return (
								<motion.div
									key={`card-${i}`}
									id={`card-${card.id}`}
									className={`relative `}
									variants={cardVariants}
									initial="remainings"
									animate={isLast ? 'current' : isUpcoming ? 'upcoming' : 'remainings'}
									exit="exit">
									<AnkiCard
										data={card}
										indexInStack={i + 1}
										isLast={isLast}
										setIsDragging={setIsDragging}
										isDragging={isDragging}
										setDirection={setDirection}
									/>
								</motion.div>
							)
						})}
					</AnimatePresence>
				</div>
			</div>
		</motion.div>
	)
}

interface PracticeItemVocabularyResult {
	is_correct: boolean
	previous_learning_stage: number
	new_learning_stage: number
	library_vocabulary: LibraryVocabulary
	learning_progress_type: PracticeVocabularyLearningProgressType
}

interface VocabularyPracticeAnswer extends PracticeSetAnswer {
	result: Record<number, PracticeItemVocabularyResult>
}

interface AnkiPracticeProps {
	practiceSet: PracticeSet
	practiceSetItems: PracticeSetItemVocabulary[]
	onPracticeAnswersSubmitted: () => void
	onClosePracticeSet: () => void
	answerResultCompleted: any
}

const AnkiPractice = ({
	practiceSet,
	practiceSetItems,
	onPracticeAnswersSubmitted,
	onClosePracticeSet,
	answerResultCompleted,
}: AnkiPracticeProps) => {
	const { t } = useTranslation()
	const [cards, setCards] = useState<PracticeSetItemVocabulary[]>(practiceSetItems)
	const [answers, setAnswers] = useState<Record<number, boolean>>({})
	const [isSubmittingAnswersLoading, setIsSubmittingAnswersLoading] = useState(false)
	const { currentPracticeStatus, availableAppLanguages, appLanguageCode } = useGlobalStore()
	const [answerResult, setAnswerResult] = useState<VocabularyPracticeAnswer>(answerResultCompleted || undefined)
	const { showModal, setMode, setData } = useWordDetailsModalStore()

	const answerDetails = useMemo(() => {
		const correctCount = Object.values(answerResult?.result || {}).filter((x) => x.is_correct).length
		const incorrectCount = Object.values(answerResult?.result || {}).filter((x) => !x.is_correct).length
		const justLearnedVocab =
			Object.values(answerResult?.result || {}).filter(
				(x) => x.learning_progress_type === PracticeVocabularyLearningProgressType.JUST_LEARNED,
			) ?? []
		const improvedLearningsVocab =
			Object.values(answerResult?.result || {}).filter(
				(x) => x.learning_progress_type === PracticeVocabularyLearningProgressType.IMPROVED_LEARNING,
			) ?? []
		const otherVocab = Object.values(answerResult?.result || {}).filter(
			(x) =>
				x.learning_progress_type !== PracticeVocabularyLearningProgressType.JUST_LEARNED &&
				x.learning_progress_type !== PracticeVocabularyLearningProgressType.IMPROVED_LEARNING,
		)

		return {
			correctCount: correctCount,
			incorrectCount: incorrectCount,
			justLearnedVocab: justLearnedVocab,
			improvedLearningsVocab: improvedLearningsVocab,
			otherVocab: otherVocab,
		}
	}, [answerResult])

	const handleOpenWordDetailsModal = (libraryVocabulary: LibraryVocabulary) => {
		setData(libraryVocabulary)
		setMode('saved/no-edit')
		showModal()
	}

	const cardsContainerVariants = {
		initial: {
			opacity: 0,
		},
		animate: {
			opacity: 1,
			transition: { duration: 2, ease: cubicBezier(0.16, 1, 0.3, 1) },
		},
		exit: {
			opacity: 0,
			transition: { duration: 0.2, ease: cubicBezier(0.7, 0, 0.84, 0) },
		},
	}

	const onCardSwiped = async (card: PracticeSetItemVocabulary, direction: string) => {
		setCards(cards.filter((x) => x.id !== card.id))
		const cardAnswer = { [card.id]: direction !== 'left' } // left: the users guess was incorrect, right was correct
		setAnswers((prev) => ({
			...prev,
			...cardAnswer,
		}))
		const completedAnswers = {
			...answers,
			...cardAnswer,
		}
		if (Object.keys(completedAnswers).length === practiceSetItems.length) {
			await onLastCardSwiped(completedAnswers)
		}
	}

	const onLastCardSwiped = async (completedAnswers: Record<number, boolean>) => {
		// all answered, submit them
		setIsSubmittingAnswersLoading(true)
		await submitAnswers(practiceSet, completedAnswers)
			.then((result: VocabularyPracticeAnswer) => {
				setAnswerResult(result)
				onPracticeAnswersSubmitted()
			})
			.catch((error) => {
				toast(error.data?.message || 'Unknown error')
			})
			.finally(() => setIsSubmittingAnswersLoading(false))
	}

	const submitAnswers = async (practiceSet: PracticeSet, completedAnswers: Record<number, boolean>) => {
		return await api(`/v2/practice/sets/${practiceSet.id}/answer`, {
			method: 'POST',
			body: {
				inputs: Object.fromEntries(
					Object.entries(completedAnswers).map(([id, correct]) => [id, { answer: correct }]),
				),
			},
		})
	}

	return (
		<div className="h-full flex flex-col relative">
			<div className="px-8">
				<div className="flex flex-col justify-center gap-2">
					<div className="flex flex-col">
						<div className="flex items-center mt-2">
							<div className="border-t border-2 border-gray-400 flex-grow"></div>
							<div className="px-3">{t('Anki')}</div>
							<div className="border-t border-2 border-gray-400 flex-grow"></div>
						</div>
					</div>
					{currentPracticeStatus != 'finished' && (
						<>
							<div>
								<p className="text-sm">
									{t(
										'Guess the translation for the word, then tap the card to see if you are right.',
									)}
								</p>
							</div>
						</>
					)}
				</div>
			</div>
			<AnimatePresence mode="wait">
				{isSubmittingAnswersLoading ? (
					<motion.div
						key="anki-submit-loading"
						className="flex flex-col justify-center items-center gap-2 h-full">
						<span>{t('Submitting')}</span>
						<Loader2 className="h-4 w-4 animate-spin" />
					</motion.div>
				) : cards.length > 0 ? (
					<motion.div
						key="anki-cards-container"
						variants={cardsContainerVariants}
						initial="initial"
						animate="animate"
						exit="exit"
						className="h-auto">
						<AnkiCardsContainer cards={cards} onCardSwiped={onCardSwiped} />
					</motion.div>
				) : currentPracticeStatus === 'finished' && answerResult !== undefined ? (
					<>
						<div>
							<div className="flex flex-col items-center justify-center mb-4">
								<PracticeSetResultSummary
									correct_count={answerResult.correct_count}
									items_count={answerResult.items_count}
									practiced_at={answerResult.practiced_at || ''}
								/>
							</div>
							<div className="flex flex-row w-full" dir="ltr">
								<div className="flex flex-1 relative justify-end items-end h-[11rem]">
									<AnkiResultCardStack
										side="left"
										cards={Array(answerDetails.incorrectCount)}
										rotateGap={3}
										startX={13}
										gapX={13}
										gapY={13}
									/>
								</div>
								<div className="flex flex-1 relative justify-start items-end h-[11rem]">
									<AnkiResultCardStack
										side="right"
										cards={Array(answerDetails.correctCount)}
										rotateGap={3}
										startX={13}
										gapX={13}
										gapY={13}
									/>
								</div>
							</div>
							<div className="flex flex-col items-center justify-center mt-4 select-none">
								<small>{t('Incorrect / Correct guesses')}</small>
							</div>
						</div>
						<LibraryVocabularyLearningsPracticeResult
							className="mt-2"
							answerDetails={answerDetails}
							handleOpenWordDetailsModal={handleOpenWordDetailsModal}
						/>
						<div className="w-full flex flex-col justify-center items-center gap-3 pb-3">
							<Button onClick={onClosePracticeSet} className="h-6 px-5 mt-5" variant="default">
								{availableAppLanguages[appLanguageCode].dir === 'rtl' ? (
									<ChevronRight className="h-4 w-4" />
								) : (
									<ChevronLeft className="h-4 w-4" />
								)}
								{t('Return to overview')}
							</Button>
						</div>
					</>
				) : (
					<></>
				)}
			</AnimatePresence>
		</div>
	)
}

export default AnkiPractice
